import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../components/Alert';
import { useConfirm } from '../components/Confirm';
import { leagueAPI, matchAPI } from '../services/api';
import './ManageMembers.css';

const ManageMembers = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { isAdmin: isGlobalAdmin } = useAuth();
  const { showAlert } = useAlert();
  const { showConfirm } = useConfirm();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playerMatchCount, setPlayerMatchCount] = useState({});
  const [hasMatchesPlayed, setHasMatchesPlayed] = useState(false);
  const [hasLeagueStarted, setHasLeagueStarted] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Pagination state
  const [players, setPlayers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [playersPagination, setPlayersPagination] = useState({ currentPage: 1, totalPages: 1 });
  const [pendingPagination, setPendingPagination] = useState({ currentPage: 1, totalPages: 1 });
  const [rejectedPagination, setRejectedPagination] = useState({ currentPage: 1, totalPages: 1 });
  const [membersLoading, setMembersLoading] = useState(false);

  const loadLeague = useCallback(async () => {
    try {
      // Check if this is a super league route
      const isSuperLeagueRoute = window.location.pathname.includes('/super-leagues/');
      
      let leagueResponse;
      if (isSuperLeagueRoute) {
        leagueResponse = await leagueAPI.getSuperLeague(leagueId);
      } else {
        leagueResponse = await leagueAPI.getLeague(leagueId);
      }
      
      const matchesResponse = await matchAPI.getLeagueMatches(leagueId).catch(() => ({ data: [] })); // Fallback if matches endpoint fails
      
      const leagueData = leagueResponse.data;
      const viewerIsAdmin = leagueData.isAdmin || isGlobalAdmin;
      const normalizedLeague = {
        ...leagueData,
        isAdmin: viewerIsAdmin,
      };
      
      // For super leagues, add a note about automatic subleague assignment
      if (normalizedLeague.isSuperLeague) {
        // Players are already included from the API response
        // Add a helper message for display
        normalizedLeague._isSuperLeague = true;
      }
      setLeague(normalizedLeague);
      
      const hasDirectMatches = matchesResponse.data?.length > 0 || normalizedLeague.matchCount > 0 || normalizedLeague.hasMatches;
      const hasSubLeagueMatches = Array.isArray(normalizedLeague.subLeagues)
        ? normalizedLeague.subLeagues.some(subLeague => subLeague.matchCount > 0 || subLeague.hasMatches)
        : false;
      setHasMatchesPlayed(Boolean(hasDirectMatches || hasSubLeagueMatches));

      if (normalizedLeague.startDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(normalizedLeague.startDate);
        startDate.setHours(0, 0, 0, 0);
        setHasLeagueStarted(today >= startDate);
      } else {
        setHasLeagueStarted(false);
      }
    } catch (err) {
      console.error('Failed to load league:', err);
      setError('Failed to load league');
    } finally {
      setLoading(false);
    }
  }, [leagueId, isGlobalAdmin]);

  const loadMembers = useCallback(async (type, page) => {
    try {
      setMembersLoading(true);
      const response = await leagueAPI.getLeagueMembers(leagueId, page, 50, type);
      const { members, pagination } = response.data;

      if (type === 'players') {
        setPlayers(members);
        setPlayersPagination(pagination);
        
        // Build match count map from the members data
        const matchCounts = {};
        members.forEach(member => {
          if (member.matchCount !== undefined) {
            matchCounts[member.userId] = member.matchCount;
          }
        });
        setPlayerMatchCount(matchCounts);
      } else if (type === 'pending') {
        setPendingUsers(members);
        setPendingPagination(pagination);
      } else if (type === 'rejected') {
        setRejectedUsers(members);
        setRejectedPagination(pagination);
      }
    } catch (err) {
      console.error(`Failed to load ${type} members:`, err);
      showAlert(`Failed to load ${type} members`, 'error');
    } finally {
      setMembersLoading(false);
    }
  }, [leagueId, showAlert]);

  useEffect(() => {
    loadLeague();
    loadMembers('players', 1);
    loadMembers('pending', 1);
    loadMembers('rejected', 1);
  }, [leagueId, isGlobalAdmin, loadLeague, loadMembers]);

  const allowMemberChanges = !hasMatchesPlayed && !hasLeagueStarted;
  const lockedMessage = hasLeagueStarted
    ? 'Member changes are disabled because the league has already started.'
    : 'Member changes are disabled because matches have already been played.';

  const handleRemovePlayer = async (playerId) => {
    if (!allowMemberChanges) {
      showAlert(lockedMessage, 'info');
      return;
    }
    if ((playerMatchCount[playerId] || 0) > 0 && playerId !== league.createdBy) {
      showAlert('Cannot remove player who has played matches.', 'info');
      return;
    }
    showConfirm('Are you sure you want to remove this player from the league? They will be moved back to the Interested Players list.', async () => {
      try {
        setActionLoading(true);
        await leagueAPI.removePlayer(leagueId, playerId);
        showAlert('Player removed and moved to Interested Players list. You can approve or reject them again.', 'success');
        loadLeague();
        loadMembers('players', playersPagination.currentPage);
        loadMembers('pending', 1); // Reload pending list to show the removed player
      } catch (err) {
        console.error('Remove player error:', err);
        showAlert(err.response?.data?.error || 'Failed to remove player', 'error');
      } finally {
        setActionLoading(false);
      }
    });
  };

  const handleApproveUser = async (userId) => {
    if (!allowMemberChanges) {
      showAlert(lockedMessage, 'info');
      return;
    }
    try {
      setActionLoading(true);
      await leagueAPI.acceptUsers(leagueId, [userId]);
      showAlert('User approved successfully!', 'success');
      loadLeague();
      loadMembers('players', playersPagination.currentPage);
      loadMembers('pending', pendingPagination.currentPage);
    } catch (err) {
      console.error('Approve user error:', err);
      showAlert(err.response?.data?.error || 'Failed to approve user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectUser = async (userId) => {
    if (!allowMemberChanges) {
      showAlert(lockedMessage, 'info');
      return;
    }
    try {
      setActionLoading(true);
      await leagueAPI.rejectUser(leagueId, userId);
      showAlert('User rejected.', 'success');
      loadLeague();
      loadMembers('pending', pendingPagination.currentPage);
      loadMembers('rejected', rejectedPagination.currentPage);
    } catch (err) {
      console.error('Reject user error:', err);
      showAlert(err.response?.data?.error || 'Failed to reject user', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="page-container">
        <div className="error-message">{error || 'League not found'}</div>
      </div>
    );
  }

  if (!league.isAdmin) {
    return (
      <div className="page-container">
        <div className="error-message">Only the league admin can manage members.</div>
      </div>
    );
  }

  if (league.superLeagueId) {
    return (
      <div className="page-container">
        <div className="error-message">
          Subleagues cannot be edited directly. Please manage members through the parent super league.
        </div>
        <Link to={`/super-leagues/${league.superLeagueId}`} className="btn btn-primary" style={{ marginTop: '15px' }}>
          Go to Super League
        </Link>
      </div>
    );
  }

  // Pagination Controls Component
  const PaginationControls = ({ pagination, onPageChange, disabled }) => {
    if (pagination.totalPages <= 1) return null;

    const { currentPage, totalPages, hasPreviousPage, hasNextPage } = pagination;
    
    return (
      <div className="pagination-controls">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPreviousPage || disabled}
          className="btn btn-secondary btn-sm"
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNextPage || disabled}
          className="btn btn-secondary btn-sm"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="page-container">
      {actionLoading && (
        <div className="loading-overlay">
          <div className="loading-card">
            <div className="loading-spinner" />
            <div className="loading-text">Updating Members</div>
            <div className="loading-subtext">Please wait while we apply your changes.</div>
          </div>
        </div>
      )}
      <div className="manage-members-container">
        <div className="manage-members-page-header">
          <div>
            <h1>Manage Members</h1>
            <p className="subtitle">{league.name}</p>
          </div>
          <button onClick={() => {
            if (league.isSuperLeague) {
              navigate(`/super-leagues/${leagueId}`);
            } else {
              navigate(`/leagues/${leagueId}`);
            }
          }} className="btn btn-secondary">
            Back to {league.isSuperLeague ? 'Super League' : 'League'}
          </button>
        </div>

        {/* Pending Interest Requests */}
        <div className="card">
          <div className="card-header">
            <h2>Interested Players ({pendingPagination.totalItems || 0})</h2>
          </div>
          {membersLoading ? (
            <div className="loading" style={{ padding: '20px' }}>Loading...</div>
          ) : (
            <>
              <div className="members-list">
                {pendingUsers.length > 0 ? (
                  pendingUsers.map((user) => (
                    <div key={user.userId} className="member-item">
                      <div className="member-info">
                        <h3><Link to={`/players/${user.userId}`}>{user.name}</Link></h3>
                        <p>{user.email}</p>
                      </div>
                      <div className="member-actions">
                        <button
                          onClick={() => handleApproveUser(user.userId)}
                          className={`btn btn-success btn-sm ${!allowMemberChanges ? 'disabled-action-button' : ''}`}
                          disabled={!allowMemberChanges}
                          type="button"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleRejectUser(user.userId)}
                          className={`btn btn-danger btn-sm ${!allowMemberChanges ? 'disabled-action-button' : ''}`}
                          disabled={!allowMemberChanges}
                          type="button"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="empty-message">No pending requests.</p>
                )}
              </div>
              <PaginationControls 
                pagination={pendingPagination} 
                onPageChange={(page) => loadMembers('pending', page)}
                disabled={actionLoading}
              />
            </>
          )}
        </div>

        {/* Current Players */}
        <div className="card">
          <div className="card-header">
            <h2>Current Players ({playersPagination.totalItems || 0})</h2>
            {league.isSuperLeague && (
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                Players are automatically assigned to subleagues based on their skill level when approved.
              </p>
            )}
          </div>
          {membersLoading ? (
            <div className="loading" style={{ padding: '20px' }}>Loading...</div>
          ) : (
            <>
              <div className="members-list">
                {players.length > 0 ? (
                  players.map((player) => {
                    const hasPlayedMatches = playerMatchCount[player.userId] > 0;
                    return (
                      <div key={player.userId} className="member-item">
                        <div className="member-info">
                          <h3><Link to={`/players/${player.userId}`}>{player.name}</Link></h3>
                          <p>{player.email}</p>
                        </div>
                        <div className="member-actions">
                          {hasPlayedMatches && (
                            <span className="badge badge-warning">
                              {playerMatchCount[player.userId]} matches played
                            </span>
                          )}
                          {(player.userId === league.createdBy || player.userId !== league.createdBy) && (
                            <button
                              onClick={() => handleRemovePlayer(player.userId)}
                              className={`btn btn-danger btn-sm ${(!allowMemberChanges || (hasPlayedMatches && player.userId !== league.createdBy)) ? 'disabled-action-button' : ''}`}
                              disabled={!allowMemberChanges || (hasPlayedMatches && player.userId !== league.createdBy)}
                              title={
                                !allowMemberChanges
                                  ? 'Member changes are disabled because matches have already been played.'
                                  : hasPlayedMatches && player.userId !== league.createdBy
                                  ? 'Cannot remove player who has played matches'
                                  : 'Remove player'
                              }
                              type="button"
                            >
                              Remove
                            </button>
                          )}
                          {player.userId === league.createdBy && (
                            <span className="badge badge-success">Admin</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="empty-message">No players yet.</p>
                )}
              </div>
              <PaginationControls 
                pagination={playersPagination} 
                onPageChange={(page) => loadMembers('players', page)}
                disabled={actionLoading}
              />
            </>
          )}
        </div>

        {/* Rejected Users */}
        {(rejectedUsers.length > 0 || rejectedPagination.totalItems > 0) && (
          <div className="card">
            <div className="card-header">
              <h2>Rejected Users ({rejectedPagination.totalItems || 0})</h2>
              <p className="subtitle">You can still approve these users if you change your mind.</p>
            </div>
            {membersLoading ? (
              <div className="loading" style={{ padding: '20px' }}>Loading...</div>
            ) : (
              <>
                <div className="members-list">
                  {rejectedUsers.map((user) => (
                    <div key={user.userId} className="member-item rejected">
                      <div className="member-info">
                        <h3><Link to={`/players/${user.userId}`}>{user.name}</Link></h3>
                        <p>{user.email}</p>
                      </div>
                      <div className="member-actions">
                        <span className="badge badge-danger">Rejected</span>
                        <button
                          onClick={() => handleApproveUser(user.userId)}
                          className={`btn btn-success btn-sm ${!allowMemberChanges ? 'disabled-action-button' : ''}`}
                          disabled={!allowMemberChanges}
                          type="button"
                        >
                          Approve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <PaginationControls 
                  pagination={rejectedPagination} 
                  onPageChange={(page) => loadMembers('rejected', page)}
                  disabled={actionLoading}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageMembers;

